import { IChildProp } from "./type";

// token缓存，相同条件下的缓存
let tokenCache: Record<string, boolean> = {};
// 保存上一次查询条件，用于清空全局变量
let lastContext = '';

/**
 * 获取表单项的显示标签。
 *
 * @param formItem - 表单项对象，包含标签和键信息。
 * @param formatMethod - 格式化方法，用于处理键值。
 * @param field - 可选参数，指定使用的字段，默认为 'label'。
 * @returns 返回格式化后的标签字符串。如果键是数组，则拼接每个键的格式化结果。
 */
export const getShowLabel = (formItem: IChildProp, formatMethod: Function, field = 'label') => {
  const key: string = formItem[`${field}Key`];
  const text = formItem[field];
  if (Array.isArray(key)) {
    let str = '';
    key.forEach(item => {
      str += item.type === 'variable' ? item.transedKey : formatMethod({ id: item.key });
    });
    return str;
  }
  return key? formatMethod({ id: key }) : text || '';
};

const CASCADER_SELF_CATEGORY = '<self>';

/**
 * 级联列标题：locale key 走翻译，'<self>' 保持原样，未知 key 由 formatMessage 回退为原文。
 */
export const resolveCascaderCategoryLabel = (
  category: string | undefined,
  formatMessage: (descriptor: { id: string }) => string,
): string => {
  if (!category || category === CASCADER_SELF_CATEGORY) {
    return category || '';
  }
  return formatMessage({ id: category });
};

/**
 * 三级级联叶子的 parent 是 Platform（Xbox），数据源在 grandparent / cascaderDataSource。
 * 快捷选中、清空、tag 去重都必须用数据源 key，不能用二级 platform。
 */
export const getCascaderDataSourceKey = (item?: any): string => {
  if (!item) {
    return '';
  }
  if (item.cascaderDataSourceValue) {
    return item.cascaderDataSourceValue;
  }
  if (item.cascaderDataSource?.value) {
    return item.cascaderDataSource.value;
  }
  if (item.parent?.parent?.type === 'cascader') {
    return item.parent?.value || '';
  }
  return item.parent?.parent?.value || item.parent?.value || '';
};

export const getCascaderLeafIdentity = (item?: any): string =>
  `${getCascaderDataSourceKey(item)}_${item?.value || ''}`;


/**
 * 评估给定表达式在上下文中的条件是否成立。
 * 
 * @param expression - 要评估的条件表达式，格式为 "key:value" 或 "key:subkey:value"。
 * @param context - 一个对象，包含用于评估表达式的上下文数据。
 * @param isGetValue - 可选参数，指示是否返回条件值而不是布尔结果。
 * @returns 如果表达式有效且条件成立，则返回 true；否则返回 false。
 */
export const evaluateCondition = (expression: string, context: any = {}, isGetValue?: boolean): boolean => {
  // console.log(expression, context);
  if (!expression) return true;
  const [key, ...rest] = expression.split(':');
  const value = context?.[key];
  // console.log('value: ', value, 'rest: ', rest);

  if (rest.length === 1) {
    const lastRest = rest[0]?.trim();
    // console.log(lastRest, value);
    if (isGetValue) return value?.[lastRest];
    // 如果是最后一个部分，直接比较值（这里假设值已经是布尔类型，或者可以通过==比较）
    // 判断是否以[]开头，如果是的话，它的值是个数组，使用includes匹配
    if (lastRest?.startsWith('[]')) return value?.includes(lastRest.slice(2) || '');
    // 对于非布尔值
    return value?.toString() === lastRest?.toString();
  }
  // 递归处理嵌套对象或数组
  if (typeof value === 'object' && value !== null) {
    // 如果对象为空，说明这个值不存在，直接不展示
    if (Object.keys(value).length === 0) {
      return false;
    }
    return evaluateCondition(rest.join(':'), value, isGetValue);
  }
  // 如果不是对象或数组，但还有剩余条件，则条件不匹配（因为我们假设条件是嵌套的）
  return false;
};

// TODO：判断括号、子括号、多次循环合并
/**
 * 解析并评估给定的布尔表达式。
 * 
 * @param expression - 要解析的布尔表达式字符串，支持逻辑与（&&）和逻辑或（||）操作符。
 * @param context - 可选的上下文对象，用于变量的值评估，默认为空对象。
 * @returns 返回表达式的布尔值结果。
 * 
 * 该函数支持对布尔表达式中的变量进行动态评估，并根据操作符的优先级进行计算。
 * 
 * @throws {Error} 如果遇到未知的操作符，将抛出错误。
 */
export const parseAndEvaluate = (expression: string, context: any = {}): boolean => {
  const currentContext = JSON.stringify(context);
  if (currentContext !== lastContext) {
    tokenCache = {};
    lastContext = currentContext;
  }
  // 定义操作符的优先级
  const precedence: any = {
    '||': 1,
    '&&': 2,
  };

  /**
   * 以op对传入的a和b两个值进行与或非计算
   * @param op 操作符
   * @param a 第一个布尔值
   * @param b 第二个布尔值
   * @returns 返回两个布尔值计算后的记过
   */
  const applyOp = (op: string, a: boolean, b: boolean): boolean => {
    // console.log(op, a, b);
    switch (op) {
      case '||':
        return a || b;
      case '&&':
        return a && b;
      default:
        throw new Error(`Unknown operator: ${op}`);
    }
  };

  // 转换表达式为令牌数组
  const tokens = expression.trim().split(/([&&||]+)/g) || [];

  // 存储结果的数组
  const outputQueue: boolean[] = [];
  // 存储操作符的数组
  const operatorStack: string[] = [];

  tokens.forEach((token: string) => {
    if (!isNaN(Number(token)) && !/^\w+$/.test(token)) {
      // 处理数字（虽然在这个特定例子中我们不需要，但为了通用性保留）
      outputQueue.push(Boolean(Number(token)));
    } else if (precedence[token]) {
      // 处理操作符
      while (
        operatorStack.length > 0 &&
        precedence[operatorStack[operatorStack.length - 1]] >=
          precedence[token]
      ) {
        // 不断地把两个值进行与或非计算，得出的记过存入结果数组
        const op = operatorStack.pop() || '';
        const b = !!outputQueue.pop();
        const a = !!outputQueue.pop();
        outputQueue.push(applyOp(op, a, b));
      }
      operatorStack.push(token);
    } else {
      // 处理变量
      const val = token.replace(/^[!|?]/, ''); // 去除开头的'!'（如果存在）
      const negate = token.startsWith('!'); // 记录是否需要取反
      const isGetValue = token.startsWith('?'); // 记录是否取值
      const result = Object.prototype.hasOwnProperty.call(tokenCache, val) ? tokenCache[val] ?? !!tokenCache[val] : evaluateCondition(val, context, isGetValue);
      tokenCache[val] = result;
      // console.log(tokenCache);
      // console.log('token', token, ',negate:', negate, ',exp:', val, ',result:', result);
      outputQueue.push(negate ? !result : result);
    }
  });

  // 应用剩余的操作符
  while (operatorStack.length > 0) {
    const op = operatorStack.pop() || '';
    const b = !!outputQueue.pop();
    const a = !!outputQueue.pop();
    outputQueue.push(applyOp(op, a, b));
  }

  // console.log('finalResult', outputQueue);
  // 返回最终结果
  return outputQueue[0];
};
